import type { IIssueInProgressDailyTrendRow } from '@skystone/shared';

type WipIssueType = 'Story' | 'Bug' | 'Task';

/**
 * ISO week helpers — Monday-start, UTC.
 * Per decisions.md Q3, all weekly bucketing on Metric View uses ISO weeks.
 */

function toUtcDate(iso: string): Date {
  const d = new Date(iso);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function isoWeekStart(iso: string): string {
  const d = toUtcDate(iso);
  const dayOfWeek = d.getUTCDay();
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d.toISOString().slice(0, 10);
}

export interface IWipWeeklyPoint {
  week: string;
  Story: number;
  Bug: number;
  Task: number;
}

/**
 * Bucket daily WIP rows into ISO weeks. Within a week and issue type we take
 * the AVERAGE of the daily snapshots (WIP is a stock metric — averaging
 * reflects the typical level better than summing would).
 *
 * The input may contain one row per (snapshotDate, issueType, teamName) —
 * since PRODOPSAI-418 `getWipTrend` returns a per-team breakdown. To produce
 * a single daily total per (date, type), per-team rows are first SUMMED
 * across teams. Without that collapse step, averaging across rows would
 * under-count by a factor of N (number of teams in scope). See PRODOPSAI-422.
 */
export function bucketDailyWipToWeekly(rows: IIssueInProgressDailyTrendRow[]): IWipWeeklyPoint[] {
  // Step 1: collapse per-team rows into one daily total per (date, type).
  const dailyTotals = sumDailyTotalsByType(rows);

  // Step 2: bucket the daily totals into ISO weeks, averaging across days.
  const byWeekAndType = new Map<string, Map<WipIssueType, number[]>>();
  for (const [date, perType] of dailyTotals) {
    const week = isoWeekStart(date);
    let inner = byWeekAndType.get(week);
    if (!inner) {
      inner = new Map();
      byWeekAndType.set(week, inner);
    }
    for (const [type, total] of perType) {
      let bucket = inner.get(type);
      if (!bucket) {
        bucket = [];
        inner.set(type, bucket);
      }
      bucket.push(total);
    }
  }

  const weeks = Array.from(byWeekAndType.keys()).sort((a, b) => a.localeCompare(b));
  return weeks.map((week) => {
    const inner = byWeekAndType.get(week) ?? new Map<WipIssueType, number[]>();
    return {
      week,
      Story: average(inner.get('Story')),
      Bug: average(inner.get('Bug')),
      Task: average(inner.get('Task')),
    };
  });
}

/**
 * Collapse per-team rows into one total per (snapshotDate, normalized type)
 * by summing `distinctCount`. Rows whose `issueType` is not Story/Bug/Task
 * are dropped. `teamName === null` rows are included (items without a team
 * still count toward the daily total).
 *
 * Exported so `aggregation.monthlyAverageByType` can share the same
 * collapse step without duplicating logic.
 */
export function sumDailyTotalsByType(
  rows: IIssueInProgressDailyTrendRow[],
): Map<string, Map<WipIssueType, number>> {
  const byDate = new Map<string, Map<WipIssueType, number>>();
  for (const row of rows) {
    const type = normalizeIssueType(row.issueType);
    if (!type) continue;
    let perType = byDate.get(row.snapshotDate);
    if (!perType) {
      perType = new Map();
      byDate.set(row.snapshotDate, perType);
    }
    perType.set(type, (perType.get(type) ?? 0) + row.distinctCount);
  }
  return byDate;
}

function normalizeIssueType(raw: string): WipIssueType | null {
  const lower = raw.toLowerCase();
  if (lower === 'story') return 'Story';
  if (lower === 'bug') return 'Bug';
  if (lower === 'task') return 'Task';
  return null;
}

function average(xs: number[] | undefined): number {
  if (!xs || xs.length === 0) return 0;
  const sum = xs.reduce((acc, n) => acc + n, 0);
  return Math.round(sum / xs.length);
}
